import React from "react";
import { Grid, Box, Stack, Avatar } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import CustomContainer from "../layouts/container";
import HelmetTag from "../components/Shared/HelmetComponent";
import {
  Header,
  SubHeader,
  SubTitle,
  Paragraph,
  Caption,
} from "../components/Shared/TypographyComponent";
import Divider from "../components/Shared/DividerComponent";
import BlogCard from "../components/Blog/BlogCard";
import CodeBlock from "../components/Blog/CodeBlock";
import BLOG_POSTS from "../data/blogPosts";

/*
Individual Blog Post Page
-------------------------
Displays a single blog post with full content
*/

function BlogPostContent({ post }) {
  const theme = useTheme();

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Format inline text (bold, inline code, links, etc.)
  const formatInlineText = (text, theme) => {
    // First split by inline code (backticks), then by links, then by bold
    const codeParts = text.split(/(`[^`\n]+?`)/g);

    return codeParts.map((codePart, i) => {
      // If it's inline code
      if (codePart.startsWith("`") && codePart.endsWith("`")) {
        return (
          <Box
            component="code"
            key={i}
            sx={{
              backgroundColor: `${theme.palette.primary.secondary}1A`, // secondary color at 10% opacity
              border: `1px solid ${theme.palette.primary.tertiary}33`, // sky blue at 20% opacity
              borderRadius: "4px",
              padding: "2px 6px",
              fontFamily: '"Fira Code", "Consolas", "Monaco", monospace',
              fontSize: "0.9em",
              color: theme.palette.primary.tertiary, // Always sky blue (#17D1FA)
              whiteSpace: "nowrap",
            }}
          >
            {codePart.slice(1, -1)}
          </Box>
        );
      }

      // Check for links [text](url)
      const linkParts = codePart.split(/(\[[^\]]+\]\([^)]+\))/g);
      return linkParts.map((linkPart, j) => {
        const linkMatch = linkPart.match(/\[([^\]]+)\]\(([^)]+)\)/);
        if (linkMatch) {
          const [, linkText, linkUrl] = linkMatch;
          return (
            <Box
              component="a"
              key={`${i}-${j}`}
              href={linkUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: theme.palette.primary.tertiary,
                textDecoration: "underline",
                fontWeight: 500,
                "&:hover": {
                  opacity: 0.8,
                },
              }}
            >
              {linkText}
            </Box>
          );
        }

        // Otherwise check for bold
        const boldParts = linkPart.split(/(\*\*[^\n*]+?\*\*)/g);
        return boldParts.map((part, k) => {
          if (part.startsWith("**") && part.endsWith("**")) {
            return (
              <Box
                component="strong"
                key={`${i}-${j}-${k}`}
                sx={{
                  fontWeight: 900,
                  fontSize: "inherit",
                  letterSpacing: "-0.01em",
                  fontFamily: "Montserrat",
                }}
              >
                {part.slice(2, -2)}
              </Box>
            );
          }
          return <span key={`${i}-${j}-${k}`}>{part}</span>;
        });
      });
    });
  };

  // Format text with line breaks and bullets
  const formatText = (text) => {
    // Split by double newlines for paragraphs
    const paragraphs = text.split("\n\n");

    return paragraphs.map((para, index) => {
      // Check if it's a bullet list (lines starting with - or * followed by space)
      if (para.trim().match(/^[-*]\s/m)) {
        const items = para
          .split("\n")
          .map((line) => line.trim())
          .filter((line) => line.match(/^[-*]\s/))
          .map((line) => line.replace(/^[-*]\s+/, "")); // Remove bullet marker and any spaces

        return (
          <Box
            component="ul"
            key={index}
            sx={{ pl: 3, my: 2, listStyleType: "disc" }}
          >
            {items.map((item, i) => (
              <Paragraph
                component="li"
                key={i}
                sx={{ mb: 1, display: "list-item", lineHeight: 1.7 }}
              >
                {formatInlineText(item, theme)}
              </Paragraph>
            ))}
          </Box>
        );
      }

      // Regular paragraph with line breaks
      return (
        <Paragraph
          key={index}
          sx={{
            lineHeight: 1.7,
            opacity: 0.85,
            mb: para !== paragraphs[paragraphs.length - 1] ? 2 : 0,
          }}
        >
          {para.split("\n").map((line, i, arr) => (
            <React.Fragment key={i}>
              {formatInlineText(line, theme)}
              {i < arr.length - 1 && <br />}
            </React.Fragment>
          ))}
        </Paragraph>
      );
    });
  };

  // Get 3 related posts (same category, excluding current)
  const relatedPosts = BLOG_POSTS.filter((p) => {
    if (p.id === post.id) return false; // Exclude current post

    // Handle both old category (string) and new categories (array) format
    const currentPostCategories = Array.isArray(post.categories)
      ? post.categories
      : [post.category];
    const relatedPostCategories = Array.isArray(p.categories)
      ? p.categories
      : [p.category];

    // Check if there's any overlap between categories
    return currentPostCategories.some((category) =>
      relatedPostCategories.includes(category)
    );
  }).slice(0, 3);

  return (
    <>
      {/* Blog Post Helmet Tag */}
      <HelmetTag
        title={`${post.title} - Nasir Abdirahman`}
        description={post.excerpt}
        href={`https://www.nasirabdirahman.com/blog/${post.slug}`}
      />

      {/* Title */}
      <CustomContainer sx={{ pt: 15, pb: 10 }}>
        {/* Title */}
        <Grid container justifyContent="center" sx={{ mb: 5 }}>
          <Grid item xs={12} md={10} lg={8}>
            <Header sx={{ fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" } }}>
              {post.title}
            </Header>
          </Grid>
        </Grid>

        {/* Featured Image */}
        <Grid container justifyContent="center" sx={{ mb: 5 }}>
          <Grid item xs={12} md={10} lg={8}>
            <Box
              component="img"
              src={post.thumbnail}
              alt={post.title}
              sx={{
                width: "100%",
                borderRadius: "8px",
                objectFit: "cover",
              }}
            />
          </Grid>
        </Grid>

        {/* Metadata */}
        <Grid container justifyContent="center" sx={{ mb: 7 }}>
          <Grid item xs={12} md={10} lg={8}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 2, sm: 4 }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
            >
              <Caption sx={{ opacity: 0.6 }}>{formatDate(post.date)}</Caption>

              {post.updatedAt && (
                <Caption sx={{ opacity: 0.6 }}>
                  Updated: {formatDate(post.updatedAt)}
                </Caption>
              )}

              <Stack direction="row" spacing={1} alignItems="center">
                <Avatar sx={{ width: 24, height: 24, bgcolor: "#17D1FA" }}>
                  N
                </Avatar>
                <Caption>{post.author}</Caption>
              </Stack>
            </Stack>
          </Grid>
        </Grid>

        {/* Lead Paragraph */}
        <Grid container justifyContent="center" sx={{ mb: 5 }}>
          <Grid item xs={12} md={10} lg={8}>
            <Paragraph sx={{ lineHeight: 1.7, opacity: 0.85 }}>
              {post.content.lead}
            </Paragraph>
          </Grid>
        </Grid>

        {/* Body Sections */}
        {post.content.sections.map((section, index) => (
          <Grid container justifyContent="center" sx={{ mb: 5 }} key={index}>
            <Grid item xs={12} md={10} lg={8}>
              <SubTitle sx={{ mb: 2, fontWeight: 600 }}>
                {section.heading}
              </SubTitle>
              {formatText(section.body)}

              {/* Render code block if present */}
              {section.code && (
                <CodeBlock
                  language={section.code.language}
                  code={section.code.snippet}
                  showLineNumbers={section.code.showLineNumbers !== false}
                />
              )}

              {/* Render additional body content after code block (body2) */}
              {section.body2 && (
                <Box sx={{ mt: 3 }}>
                  {formatText(section.body2)}
                </Box>
              )}
            </Grid>
          </Grid>
        ))}

        {/* Summary */}
        {post.content.summary && post.content.summary.length > 0 && (
          <Grid container justifyContent="center" sx={{ mb: 5 }}>
            <Grid item xs={12} md={10} lg={8}>
              <Box
                sx={{
                  backgroundColor: "rgba(23, 209, 250, 0.1)",
                  border: "1px solid rgba(23, 209, 250, 0.3)",
                  borderRadius: "8px",
                  p: 4,
                }}
              >
                <SubTitle sx={{ mb: 3, color: "#17D1FA" }}>Summary</SubTitle>
                <Stack spacing={2} component="ul" sx={{ pl: 3 }}>
                  {post.content.summary.map((point, index) => (
                    <Paragraph
                      component="li"
                      key={index}
                      sx={{ lineHeight: 1.6 }}
                    >
                      {point}
                    </Paragraph>
                  ))}
                </Stack>
              </Box>
            </Grid>
          </Grid>
        )}
      </CustomContainer>

      {/* Divider */}
      <Divider color="#17D1FA" />

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <CustomContainer sx={{ pt: 10, pb: 15 }}>
          <Grid container sx={{ mb: 5 }}>
            <SubHeader>Related Posts</SubHeader>
          </Grid>

          <Grid container spacing={3}>
            {relatedPosts.map((relatedPost) => (
              <Grid item xs={12} sm={6} md={4} key={relatedPost.id}>
                <BlogCard post={relatedPost} />
              </Grid>
            ))}
          </Grid>
        </CustomContainer>
      )}
    </>
  );
}

/* Blog Post Container */
function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();

  // Find post by slug
  const post = BLOG_POSTS.find((p) => p.slug === slug);

  // If post not found, redirect to blog
  React.useEffect(() => {
    if (!post) {
      navigate("/blog");
    }
  }, [post, navigate]);

  if (!post) {
    return null;
  }

  return <BlogPostContent post={post} />;
}

export default BlogPost;
